import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MessageSquare, Smartphone, ArrowRight, Zap, Brain, Clock,
  Target, Bot, Send, CheckCircle, Copy, RefreshCw,
  Mail, Hash, MessageCircle, Terminal, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsOpenClawOverview() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const architectureDiagram = `User Message (Telegram / WhatsApp / iMessage / Slack / Email)
    │
    ▼
┌──────────────────────────────┐
│   OpenClaw Gateway           │
│   (TypeScript / Node.js)     │
│   • Platform adapters        │
│   • Message normalization    │
│   • Session management       │
│   • Semantic memory          │
└──────────┬───────────────────┘
           │ REST API
           ▼
┌──────────────────────────────┐
│   Commerce Index Backend     │
│   (Python / FastAPI)         │
│   • Command routing          │
│   • Agent orchestration      │
│   • Business logic           │
│   • Data access layer        │
└──────────┬───────────────────┘
           │
           ▼
┌──────────────────────────────┐
│   Agent System               │
│   • 1 CEO + 13 directors      │
│   • 60+ specialist agents     │
│   • Trust ladder checks      │
│   • Guardrail enforcement    │
│   • Decision logging         │
└──────────┬───────────────────┘
           │
           ▼
┌──────────────────────────────┐
│   Response                   │
│   • Formatted for platform   │
│   • Markdown / rich text     │
│   • Action confirmations     │
│   • Inline buttons (where    │
│     supported)               │
└──────────────────────────────┘`;

  const commandExamples = `/status              → Full store health overview
/price PRODUCT_SKU   → Check or adjust a product price
/deals               → List active deals
/deal create         → Create a new deal interactively
/inventory           → Inventory summary with low-stock alerts
/agents              → Agent status and recent actions
/brief               → Today's daily brief on demand
/goal set            → Set a new business goal
/approve 3           → Approve pending agent action #3
/sales today         → Sales summary for today`;

  const platforms = [
    {
      icon: Send,
      name: 'Telegram',
      desc: 'Full-featured with inline buttons, rich formatting, and instant delivery. The recommended platform for power users.',
      status: 'Full support',
      color: '#38BDF8',
    },
    {
      icon: MessageCircle,
      name: 'WhatsApp Business',
      desc: 'Business API integration with template messages, media support, and end-to-end encryption.',
      status: 'Full support',
      color: '#3EF1C7',
    },
    {
      icon: MessageSquare,
      name: 'iMessage',
      desc: 'Via Mac Mini relay. Native iMessage integration for Apple ecosystem users. Requires a dedicated Mac Mini.',
      status: 'Mac Mini required',
      color: '#94A3B8',
    },
    {
      icon: Hash,
      name: 'Slack',
      desc: 'Workspace integration with slash commands, threaded replies, and channel-based notifications.',
      status: 'Full support',
      color: '#7A5CFF',
    },
    {
      icon: Mail,
      name: 'Email',
      desc: 'Send commands and receive reports via email. Great for daily briefs and weekly summaries.',
      status: 'Full support',
      color: '#FFD166',
    },
  ];

  const keyFeatures = [
    {
      icon: Terminal,
      title: '30+ Commands',
      desc: 'Comprehensive command set covering products, deals, inventory, agents, analytics, goals, and admin. Natural language fallback for anything not covered by a command.',
    },
    {
      icon: RefreshCw,
      title: 'Cross-Platform Sync',
      desc: 'Start a conversation on Telegram, continue on WhatsApp. All platforms share the same session state, message history, and pending actions.',
    },
    {
      icon: Brain,
      title: 'Semantic Memory',
      desc: 'OpenClaw remembers context from previous conversations. Reference "that product we discussed yesterday" and it knows what you mean.',
    },
    {
      icon: Clock,
      title: 'Daily Briefs',
      desc: 'Automated morning reports delivered to your preferred platform. Sales summary, agent actions, inventory alerts, and key metrics — all in one message.',
    },
    {
      icon: Target,
      title: 'Goal Tracking',
      desc: 'Set revenue goals, inventory targets, or customer satisfaction benchmarks. OpenClaw tracks progress and proactively updates you.',
    },
    {
      icon: Bot,
      title: 'Agent Management',
      desc: 'Enable, disable, and configure agents directly from chat. Review and approve pending agent actions without opening the dashboard.',
    },
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 mb-4">
          OpenClaw
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          OpenClaw: Messaging-First Commerce
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Manage your entire commerce operation from your phone. No dashboard required. OpenClaw is the
          messaging gateway that connects Telegram, WhatsApp, iMessage, Slack, and Email to Commerce Index.
        </p>
      </div>

      {/* Why Messaging-First */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Why Messaging-First?
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Smartphone className="w-8 h-8 text-[#3EF1C7] mb-2" />
              <CardTitle className="text-white text-lg">Manage from Your Phone</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Check sales, approve agent actions, create deals, and monitor inventory — all from the
                messaging app you already use every day. No browser, no login, no dashboard.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Zap className="w-8 h-8 text-[#FFD166] mb-2" />
              <CardTitle className="text-white text-lg">Instant Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Get notified the moment something needs attention: low stock, a spike in returns,
                an agent requesting approval, or a competitor price change. React in seconds.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Clock className="w-8 h-8 text-[#7A5CFF] mb-2" />
              <CardTitle className="text-white text-lg">No Dashboard Needed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                The dashboard is powerful, but not everyone wants to stare at a screen. OpenClaw gives you
                80% of the functionality via simple text commands.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Architecture */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Architecture
        </h2>
        <p className="text-[#94A3B8] mb-4">
          OpenClaw is a standalone TypeScript/Node.js service that acts as a gateway between messaging
          platforms and the Commerce Index backend:
        </p>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(architectureDiagram)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors z-10"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{architectureDiagram}</code>
          </pre>
        </div>
        <div className="mt-4 bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <p className="text-sm text-[#94A3B8]">
            <span className="text-white font-medium">Key design principle:</span> OpenClaw normalizes messages
            from all platforms into a unified format before sending to the backend. This means the same
            command works identically on every platform — <code className="text-[#3EF1C7]">/status</code> on Telegram
            returns the same data as <code className="text-[#3EF1C7]">/status</code> on WhatsApp or Slack.
          </p>
        </div>
      </section>

      {/* Supported Platforms */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Supported Platforms
        </h2>
        <div className="space-y-3">
          {platforms.map((platform, i) => {
            const Icon = platform.icon;
            return (
              <div key={i} className="flex items-start gap-4 p-4 bg-[#0D1628] border border-[#22304A] rounded-lg">
                <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: platform.color }} />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-white font-medium">{platform.name}</h3>
                    <Badge
                      className="text-xs"
                      style={{
                        backgroundColor: `${platform.color}20`,
                        color: platform.color,
                        borderColor: `${platform.color}30`,
                      }}
                    >
                      {platform.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{platform.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Key Features */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Key Features
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {keyFeatures.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <Card key={i} className="bg-[#0D1628] border-[#22304A]">
                <CardContent className="p-5">
                  <Icon className="w-6 h-6 text-[#3EF1C7] mb-3" />
                  <h3 className="text-white font-medium mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#94A3B8]">{feature.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Example Commands */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Example Commands
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Here is a sample of what you can do from any messaging platform:
        </p>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(commandExamples)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors z-10"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{commandExamples}</code>
          </pre>
        </div>
        <p className="text-[#64748B] text-sm mt-3">
          See the full <Link to="/docs/commands" className="text-[#3EF1C7] hover:underline">Command Reference</Link> for
          all 30+ commands with examples and parameters.
        </p>
      </section>

      {/* Example Conversation */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Example Conversation
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Here's what a typical interaction looks like on Telegram:
        </p>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6 space-y-4">
          {[
            { from: 'user', text: '/pulse' },
            { from: 'bot', text: '📊 Store Pulse — Dec 15, 2025\n\n🟢 Commerce Health Score: 847 / 1000\n💰 Revenue today: $2,847.50 (↑ 12% vs yesterday)\n📦 Orders: 34 (↑ 8%)\n⚠️ 2 low-stock alerts\n🤖 3 agent actions pending approval\n\nType /inventory for stock details or /approve to review actions.' },
            { from: 'user', text: '/inventory' },
            { from: 'bot', text: '📦 Inventory Summary\n\n🔴 Critical (< 5 days supply):\n  • ATH-WH-001-BLK — 5 units left (reorder NOW)\n  • KBD-ERG-002 — 8 units left (3 days supply)\n\n🟡 Watch (5-14 days):\n  • HUB-7IN1-003 — 42 units (11 days supply)\n\n🟢 Healthy: 47 other SKUs\n\nThe Inventory Agent recommends reordering 2 SKUs. Type /approve 1 to confirm.' },
            { from: 'user', text: '/approve 1' },
            { from: 'bot', text: '✅ Approved: Reorder ATH-WH-001-BLK\n\nQuantity: 200 units\nSupplier: AudioTech Direct\nEstimated delivery: Dec 22\nCost: $4,200.00\n\nPurchase order generated. You\'ll get a tracking update when it ships.' },
          ].map((msg, i) => (
            <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] p-3 rounded-lg text-sm whitespace-pre-line ${
                  msg.from === 'user'
                    ? 'bg-[#3EF1C7]/20 text-[#3EF1C7] border border-[#3EF1C7]/30'
                    : 'bg-[#1E293B] text-[#E2E8F0] border border-[#334155]'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Getting Started */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Getting Started with OpenClaw
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/channel-setup">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-5">
                <MessageSquare className="w-6 h-6 text-[#3EF1C7] mb-3" />
                <h3 className="text-white font-medium mb-2">Channel Setup Guide</h3>
                <p className="text-sm text-[#94A3B8]">
                  Step-by-step instructions for connecting Telegram, WhatsApp, iMessage, Slack, or Email
                  to your Commerce Index account.
                </p>
                <span className="text-[#3EF1C7] text-sm mt-3 inline-flex items-center gap-1">
                  Set up now <ArrowRight className="w-3 h-3" />
                </span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/commands">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-5">
                <Terminal className="w-6 h-6 text-[#7A5CFF] mb-3" />
                <h3 className="text-white font-medium mb-2">Command Reference</h3>
                <p className="text-sm text-[#94A3B8]">
                  Complete reference for all 30+ commands with usage examples, parameters, and expected
                  responses. Bookmark this one.
                </p>
                <span className="text-[#3EF1C7] text-sm mt-3 inline-flex items-center gap-1">
                  View commands <ArrowRight className="w-3 h-3" />
                </span>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Channel Setup Guide', link: '/docs/channel-setup' },
                { text: 'Full Command Reference', link: '/docs/commands' },
                { text: 'Account Linking & OTP', link: '/docs/account-linking' },
                { text: 'Meet the agent org chart', link: '/docs/agents' },
                { text: 'Onboarding Checklist', link: '/docs/onboarding-checklist' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
